
/// <summary>
/// DailyPlausibility Widget (Client-Side)
///
/// This jQuery UI widget provides the client-side user interface for managing daily
/// plausibility rules for a single analog signal. It communicates with the server-side
/// C# class "DailyPlausibility" (VCaVisual) via AJAX using the inherited getData()
/// and updateData() methods from vcBaseV4.
///
/// The widget layout consists of two views:
///   - LIST VIEW: Displays all configured plausibility entries in a scrollable table.
///     Each row shows the number of days, min/max plausibility thresholds, and a
///     human-readable activation time description (months and weekdays). Rows are
///     clickable to open the detail/edit view. A delete button allows removal.
///   - DETAIL VIEW: A form for creating or editing a single plausibility entry.
///     Contains input fields for days, min/max values, and a calendar filter widget
///     (vcCalendarFilter) for selecting active months and weekdays.
///
/// Supported operations:
///   - Fetch: Loads all entries from the server (TypeId: 1)
///   - Create: Adds a new entry to the list and persists it
///   - Edit: Modifies an existing entry and persists the change
///   - Delete: Removes an entry (with confirmation dialog) and persists the change
///
/// All persist operations send the complete list to the server (full replacement strategy).
///
/// <br/>
/// Author: AFU<br />
/// Date: 20.1.2026<br />
/// Copyright ©: EUDT GmbH 2026 <br />
/// Version-History:<br />
/// **********************************************************************<br />
/// *** v1.0.0 - 4.6.2026                                              *<br />
/// **********************************************************************<br />
/// * - basic functionality                                              *<br />
/// **********************************************************************<br />
/// </summary>


jQuery.widget("bluaul.DailyPlausibility", jQuery.common.vcBaseV4, {

    // Stores the server-fetched data. The 'list' property holds an array of
    // DailyPlausibilityEntry objects (each with Days, Timer, MinPlaus, MaxPlaus, ActivationTimes).
    myData: {
        list: null,
    },

    // Tracks the current UI/editing state:
    //   - index: The array index of the currently selected/edited entry (-1 = new entry mode).
    //   - decimals: The number of decimal places used for formatting min/max plausibility values.
    myStatus: {
        index: -1,
        decimals: 3
    },

    /**
     * Widget constructor. Registers this visual component with the framework
     * using type ID 407 (unique identifier for DailyPlausibility) and invokes
     * the parent class (vcBaseV4) constructor to set up inherited functionality.
     */
    _create: function () {
        this._createVisual(407);
        this._super();
    },

    /**
     * Initializes the HTML structure of the widget.
     * Called by the framework after the widget is created. This method:
     *   1. Locates the root container element by its CSS class (.dc-daily-plaus).
     *   2. Builds the complete DOM tree (menu bar, list panel, detail/edit panel).
     *   3. Calculates and applies responsive heights and widths based on the visual's total size.
     *
     * @returns {boolean} true if initialization succeeded, false on error.
     */
    _initHTML: function () {
        var me = this;
        try {
            // Locate the root container element by its CSS class.
            me._setRoot(".dc-daily-plaus");

            // Construct the full DOM tree (menu bar, list panel, detail panel).
            me._buildHtml();

            // Calculate and apply element dimensions relative to the visual's total size.
            me._setHeightsAndWidths();
        } catch (e) { me._writeErr(e); return false; }
        return true;
    },
    /**
     * Initializes all interactive event handlers and triggers the first data fetch.
     * Called by the framework after _initHTML completes. This method:
     *   1. Binds click/keyup/paste events for buttons, list items, and form inputs.
     *   2. Triggers the initial AJAX fetch to load existing plausibility entries from the server.
     *
     * @returns {boolean} true if initialization succeeded, false on error.
     */
    _initEvents: function () {
        var me = this;
        try {

            // Bind all interactive event handlers (buttons, list clicks, date picker).
            me._initStructure();

            // Trigger the first server fetch for the initial day.
            me._fetch();

        } catch (e) { me._writeErr(e); return false; }
        return true;
    },

    /**
     * Builds the complete HTML/DOM structure for the widget.
     * Creates all UI elements without binding events or fetching data.
     *
     * Structure:
     *   - Menu bar: Contains the "New" button for adding a new plausibility entry.
     *   - Content area:
     *     - List panel: Header row (Days | Min | Max | Calendar) + scrollable entry rows.
     *     - Detail panel (initially hidden): Back/Save buttons, input form (days, min, max),
     *       and the vcCalendarFilter widget for selecting active months/weekdays.
     *
     * All DOM references are stored in me.myHtml for efficient access by other methods.
     *
     * @returns {boolean} true if the DOM was built successfully, false on error.
     */
    _buildHtml: function () {
        var me = this;
        try {
            // Master reference object holding all DOM element references for easy access.
            me.myHtml = {
                menu: {}, content: { details: {}, list: {} }
            };

            // ----- TOP MENU BAR -----
            // Contains the reload button and the date selector.
            let obj = me.myHtml.menu;
            obj.wrapper = me._setObj("", "vc-grid-margin-bottom").css("width", "100%").css("height", "30px");
            obj.new = me._setObj("btn-new", "").css("float", "left").html(me.specific.resources.new);
            obj.wrapper.append(obj.new, obj.date);

            // ----- CONTENT AREA -----
            obj = me.myHtml.content;
            obj.wrapper = me._setObj("", "").css("width", "100%");
            me.myRoot.append(me.myHtml.menu.wrapper, me.myHtml.content.wrapper);

            // ----- LEFT COLUMN: Entry List -----
            // Displays clickable rows of edit log entries for the selected day.
            obj = me.myHtml.content.list;
            obj.wrapper = me._setObj("", "vc-list").css("overflow-y", "hidden");

            // List header with "Entries" label.
            obj.header = me._setListHeader("");
            var line = me._setListItem();
            line.column.append(me._setListElement("", "w10 text-right").html(me.specific.resources.days));
            line.column.append(me._setListElement("", "w15 text-right").html(me.specific.resources.minimum));
            line.column.append(me._setListElement("", "w15 text-right").html(me.specific.resources.maximum));
            line.column.append(me._setListElement("", "w3").html("<br/>"));
            line.column.append(me._setListElement("", "w50").html(me.specific.resources.calendar));
            line.column.append(me._setListElement("", "w3").html("<br/>"));
            obj.header.append(line.item);

            // Scrollable container where entry rows are dynamically inserted.
            obj.entries = me._setObj("entries", "vc-list-table").css("border", "1px solid #ddd");
            obj.wrapper.append(obj.header, obj.entries);
            me.myHtml.content.wrapper.append(obj.wrapper);

            //  edit form 
            obj = me.myHtml.content.details;
            obj.wrapper = me._setObj("", "").hide();
            obj.top = me._setWrapper("", "").css("padding-bottom", "3px");
            obj.back = me._setObj("btn-back", "").css("float", "left").html(me.specific.resources.back);
            obj.save = me._setObj("btn-save", "").css("float", "right").html(me.specific.resources.save);
            obj.top.append(obj.back, obj.save);

            obj.form = me._setObj("", "form vc-grid-2col vc-grid-margin-right float-box").css("height", "70px");
            obj.days = me._buildInputBox("days", me.specific.resources.days, 1, 90, false);
            obj.minPlaus = me._buildInputBox("minplaus", me.specific.resources.minimum, 0, 90, true);
            obj.maxPlaus = me._buildInputBox("maxplaus", me.specific.resources.maximum, 0, 90, true);

            obj.form.append(obj.days.box, obj.minPlaus.box, obj.maxPlaus.box);
            obj.timer = me._setObj("", "float-box").css("width", "600px").css("height", "195px");

            obj.wrapper.append(obj.top, obj.form, obj.timer);
            me.myHtml.content.wrapper.append(obj.wrapper);

        } catch (e) { me._writeErr(e); return false; }
        return true;
    },
    /**
     * Creates a labeled input box element for the detail/edit form.
     * Returns an object containing references to the box container, the form item wrapper,
     * the caption label, and the input field itself.
     *
     * @param {string} type - A unique identifier suffix used for element IDs (e.g., "days", "minplaus", "maxplaus").
     * @param {string} caption - The localized label text displayed above the input field.
     * @param {number} val - The initial/default value to set in the input field.
     * @param {number} width - The pixel width for the input field.
     * @returns {object} An object with properties: box (container div), item (form item wrapper), caption (label), input (input element).
     */
    _buildInputBox: function (type, caption, val, width, float) {
        var me = this;
        try {
            var box = {};
            box.box = $("<div>").addClass(" vc-grid-margin-bottom " + (float ? " float-box vc-grid-margin-right": "")).css({ gap: "10px", "align-items": "flex-start" });
            box.item = me._setWrapper("div-txt-" + type, "formItem formItemValid type-text").css({ "width": (width + 20) + "px" }).removeClass("vc-grid-margin-bottom");
            box.caption = me._setObj("", "caption").html(caption).css("font-weight", "bold");
            box.input = me._setInput("txt-" + type, "value large").css("width", width + "px").val(val).css("text-align", "right");
            box.item.append(box.caption, box.input);
            box.box.append(box.item);
            return box;

        } catch (e) { me._writeErr(e); return null; }
    },

    /**
     * Binds all interactive event handlers for the widget's UI elements.
     * This includes:
     *   - "New" button: Opens the detail view in create mode (empty form).
     *   - List item click (delegated): Opens the detail view in edit mode with the clicked entry's data.
     *   - "Back" button: Returns to the list view without saving.
     *   - "Save" button: Validates and persists the current entry (create or update).
     *   - Form input keyup/paste: Triggers real-time validation to enable/disable the save button.
     *   - Delete button (delegated): Shows a confirmation dialog and removes the entry on confirm.
     *
     * Event delegation is used for list items and delete buttons to support dynamically
     * created DOM elements after data fetches.
     */
    _initStructure: function () {
        var me = this;
        try {

            me.myHtml.menu.new.off("click").on("click", function () { me._showDetails(null); }).button();

            // Entry list: delegated click handler for dynamically created list items.
            // Using delegation ensures newly added rows after a fetch are also clickable.
            me.myHtml.content.list.entries.off("click", ".vc-list-item").on("click", ".vc-list-item", function () { me._showDetails($(this)); });

            me.myHtml.content.details.back.off("click").on("click", function () { me._back(); }).button();//.button("disable");
            me.myHtml.content.details.save.off("click").on("click", function () { me._save(); }).button().button("disable");
           

            me.fnd(".form .value").off("keyup").on("keyup", function () { me._validateAll(); });
            me.fnd(".form .value").off("paste").on("paste", function () { me._validateAll(); });

            me.myHtml.content.list.entries.off("click", '.btn-delete').on("click", '.btn-delete', function (e) {
                //me.resources.deleteConfirmText
                me.openAskDialog(me.specific.resources.deleteConfirmText,
                    { at: 'center center-20%', of: window },
                    function () {
                     
                        me._delete(parseInt($(e.target).parent(".btn-delete").data("index")));
                        //}
                    });

                //
                e.stopPropagation();
            });

        } catch (e) { me._writeErr(e); }
    },
    /**
     * Switches from the list view to the detail/edit view.
     * Populates the form fields and calendar filter based on the selected entry.
     *
     * @param {jQuery|null} obj - The clicked list item jQuery element, or null to create a new entry.
     *   - If null: Enters "create" mode with default values (days=1, min=0, max=0, default calendar).
     *   - If a jQuery element: Enters "edit" mode, reading the entry data from the element's
     *     data attributes and populating all form fields accordingly.
     *
     * The calendar filter widget (vcCalendarFilter) is initialized with the entry's Timer object,
     * allowing the user to visually select active months and weekdays.
     *
     * @returns {boolean} true if the detail view was shown successfully, false on error.
     */
    _showDetails: function (obj) {
        var me = this;
        var timer, days, minPlaus, maxPlaus = null;
        try {

            var details = me.myHtml.content.details;
            me.myHtml.menu.wrapper.hide();
            me.myHtml.content.list.wrapper.hide();
            me.myHtml.content.list.wrapper.hide();
            details.wrapper.show();
           
            if (obj == null) {
                //add new entry
                me.myStatus.index = -1;
                days = 1;
                minPlaus = 0;
                maxPlaus = 0;
                timer = JSON.parse(me.specific.defaultTimer);
            } else {
                me.myStatus.index = obj.data("index");
                var entry = obj.data("entry");
                days = entry.Days;
                minPlaus = String(entry.MinPlaus).replace('.', ',');
                maxPlaus = String(entry.MaxPlaus).replace('.', ',');
                timer = entry.Timer;

            }

            details.days.input.val(days);
            details.minPlaus.input.val(minPlaus);
            details.minPlaus.input.formatDouble(me.myStatus.decimals);
            details.maxPlaus.input.val(maxPlaus);
            details.maxPlaus.input.formatDouble(me.myStatus.decimals);

            if (details.timer.hasClass("vc-calendar-filter")) {
                details.timer.vcCalendarFilter("destroy");
            }
            var options = { data: timer, hideTimer: true, hideNavigation: true,height:195, hideSelection:true, langID: me.settings.userLangIDForCulture, callback: function(){ me._validateAll(); } }
            details.timer.vcCalendarFilter(options);

        } catch (e) { me._writeErr(e); return false; }
        return true;
    },

    /**
     * Navigates back from the detail/edit view to the list view.
     * Hides the detail panel and shows the list panel without saving any changes.
     * Any unsaved modifications in the form are discarded.
     *
     * @returns {boolean} true if navigation succeeded, false on error.
     */
    _back: function () {
        var me = this;
        try {
            me.myHtml.menu.wrapper.show();
            me.myHtml.content.list.wrapper.show();
            me.myHtml.content.details.wrapper.hide();

        } catch (e) { me._writeErr(e); return false; }
        return true;
    },

    /**
     * Fetches the list of daily plausibility entries from the server via AJAX.
     * Sends a request with TypeId: 1 to the server-side getData() method, which queries
     * the database and returns a JSON array of DailyPlausibilityEntry objects.
     *
     * On success, the returned data is stored in me.myData.list and the list view is
     * re-rendered via _generateList(). On failure (null response), an error message is shown.
     *
     * A loading spinner is displayed in the list area during the request.
     *
     * @returns {boolean} true if the fetch was initiated successfully, false on error.
     */
    _fetch: function () {
        var me = this;
        try {
            // Show a loading indicator in the entry list while waiting for the response.
            me._setLoadingIcon(me.myHtml.content.list.entries);

            // AJAX call to the server-side getData method.
            // TypeId 1 corresponds to the "fetch entries" case in the C# switch statement.
            me.getData({ TypeId: 1 }, function (result) {
                if (result == null) {
                    // Server returned null → display error message to the user.
                    me.showMessage(me.resources.err_loadData, true); return true;
                }


                // Store the fetched data and render the list.
                me.myData.list = result;
                me._generateList();

            });

        } catch (e) { me._writeErr(e); return false; }
        return true;
    },
    /**
     * Renders the list of daily plausibility entries in the list panel.
     * Clears any existing content and creates a new row for each entry by calling _fillRow().
     * If the data is null or empty, an informational "no entries" message is displayed.
     * After rendering, alternating row styles are applied for visual readability.
     *
     * @returns {boolean} true if the list was rendered successfully, false if empty or on error.
     */
    _generateList: function () {
        var me = this;
        try {

            // If no data was returned or the array is empty, show informational message.
            if (me.myData.list === null || me.myData.list.length == 0) {
                me.myHtml.content.list.entries.html($("<br>")).append($("<div>").addClass("note").html(me.resources.noEntriesExist));
                return false;
            }

            // Clear existing list content before repopulating.
            me.myHtml.content.list.entries.html("");

            // Create one list row per edit log entry.
            for (i = 0; i < me.myData.list.length; i++) { me._fillRow(i); };

            // Apply alternating background colors for better readability.
            me._setAltListStyle(me.myHtml.content.list.entries);
            return true;

        } catch (e) {
            me._writeErr(e);
            return false;
        }
    },
    /**
     * Creates and appends a single list row for the entry at the given array index.
     * Each row displays:
     *   - Days: The number of days for the plausibility window.
     *   - MinPlaus: The minimum threshold, formatted with the configured decimal places.
     *   - MaxPlaus: The maximum threshold, formatted with the configured decimal places.
     *   - ActivationTimes: A human-readable text showing active months and weekdays
     *     (e.g., "monthly | daily" or "Jan, Mar | Mon, Wed").
     *   - Delete button: An icon button to remove this entry (with confirmation).
     *
     * The full entry data object and its array index are stored as jQuery data attributes
     * on the row element, enabling retrieval when the row is clicked for editing.
     *
     * @param {number} index - The zero-based index of the entry in me.myData.list to render.
     * @returns {boolean} true if the row was created successfully, false on error.
     */
    _fillRow: function (index) {
        var me = this;
        try {

     
            var line = me._setListItem();

            var row = me.myData.list[index];

            // Attach all entry data to the DOM element for later retrieval on click.
            line.item.addClass("found").data("entry", row).data("index", index);

            let html = $("<div>");
            //html.append(moment(logs[i].Timestamp).format("HH:mm") + " h: " + sFrom + " ->  " + sTo);
            line.column.append(me._setListElement("", "w10 text-right").html(row.Days + " " + me.specific.resources.days));

            let div = $("<div>").html(row.MinPlaus);
            div.formatDouble(me.myStatus.decimals);


            me.myHtml.content.details.maxPlaus.input.formatDouble(me.myStatus.decimals);
            let tmp = $("<div>").html(row.MinPlaus);
            tmp.formatDouble(me.myStatus.decimals);
            line.column.append(me._setListElement("", "w15 text-right").html(tmp.html()));
            tmp = $("<div>").html(row.MaxPlaus);
            tmp.formatDouble(me.myStatus.decimals);
            line.column.append(me._setListElement("", "w15 text-right").html(tmp.html()));
            line.column.append(me._setListElement("", "w3").html("<br/>"));
            line.column.append(me._setListElement("", "w50").html(row.ActivationTimes));
            line.column.append(me._setListElement("", "w5 text-right btn-delete").data("index", index).html(me.specific.resources.icon_delete));
           

            me.myHtml.content.list.entries.append(line.item);

            return true;
        } catch (e) { me._writeErr(e); return false; }
    },
    /**
     * Validates all input fields in the detail/edit form and updates the save button state.
     * Calls _validate() for each field (days, minPlaus, maxPlaus) with the appropriate
     * validation mode (integer vs. double). The save button is enabled only when ALL
     * fields pass validation.
     *
     * This method is triggered on every keyup and paste event in the form inputs,
     * as well as when the calendar filter changes, providing real-time feedback.
     *
     * @returns {boolean} true if all fields are valid, false otherwise.
     */
    _validateAll: function () {
        var me = this;
        var valid = false;
        try {

            valid = me._validate(me.myHtml.content.details.days, false) &&
                me._validate(me.myHtml.content.details.minPlaus, true) &&
                me._validate(me.myHtml.content.details.maxPlaus, true);
          
            me.myHtml.content.details.save.button(valid ? "enable" : "disable");
            return valid;

        } catch (e) { me.writeError('validate', e); return false; }
        return false;
    },
    /**
     * Validates a single input field and applies visual feedback (valid/invalid CSS class).
     * Performs the following checks:
     *   1. The value is not empty.
     *   2. The value passes numeric validation (validateNumeric + parseFloat).
     *   3. The value matches the expected format:
     *      - Integer mode (isDouble=false): Digits only, optional thousands separators (dots).
     *      - Double mode (isDouble=true): Digits with optional thousands separators and decimal comma.
     *
     * The form item wrapper receives either 'formItemValid' or 'formItemInvalid' CSS class
     * to provide visual feedback to the user.
     *
     * @param {object} obj - The input box object (as created by _buildInputBox) with .input and .item references.
     * @param {boolean} isDouble - If true, allows decimal comma in the value; if false, only integers are accepted.
     * @returns {boolean} true if the value is valid, false otherwise.
     */
    _validate: function (obj, isDouble) {
        var me = this;
        var val;
        try {

            val = me.vcC.cleanNumber(obj.input.val());
          
            let valid = val.length > 0; 
            if (valid) {
                valid = !(validateNumeric(val) === false || isNaN(parseFloat(val))); 
            }

            var trimmed = obj.input.val().trim();
            // Optional negativ, Tausenderpunkte, Dezimalkomma
            if (isDouble) {
                valid = /^-?(\d{1,3}(\.\d{3})*|\d+)(,\d+)?$/.test(trimmed);
            } else {
                valid =  /^-?(\d{1,3}(\.\d{3})*|\d+)$/.test(trimmed);
            }

            obj.item.removeClass("formItemInvalid").removeClass("formItemValid");
            obj.item.addClass(valid ? "formItemValid" : "formItemInvalid");


            return valid;

        } catch (e) { me.writeError('validate', e); return false; }
        return false;
    },
    /**
     * Saves the current entry (create or update) by persisting the complete list to the server.
     *
     * This method:
     *   1. Reads the current calendar filter state from the vcCalendarFilter widget.
     *   2. Reads and parses the days, minPlaus, and maxPlaus values from the form inputs.
     *      (Converts German number format: removes thousand separators, replaces comma with dot.)
     *   3. If myStatus.index == -1: Appends a new entry to the list (create mode).
     *      Otherwise: Updates the existing entry at the stored index (edit mode).
     *   4. Sends the complete updated list to the server via updateData() (TypeId: 1).
     *   5. On success: Disables save button, navigates back to list view, and re-fetches data.
     *      On failure: Shows an error message to the user.
     *
     * @returns {boolean} true if the save was initiated, false on error.
     */
    _save: function () {
        var me = this;
        try {

            var timer = me.myHtml.content.details.timer.vcCalendarFilter("getData");
            var days = parseInt(me.myHtml.content.details.days.input.val());
            var minPlaus = parseFloat(me.myHtml.content.details.minPlaus.input.val().replace(".", "").replace(",", "."));
            var maxPlaus = parseFloat(me.myHtml.content.details.maxPlaus.input.val().replace(".", "").replace(",", "."));

            var i = me.myStatus.index;

            var typeid = -1;
            if (i == -1) {
                typeid = 1;
                //add new entry
                me.myData.list.push({ Days: days, MinPlaus: minPlaus, MaxPlaus: maxPlaus, Timer: timer });
            } else {
                typeid = 2;
            
                me.myData.list[i].Days = days;
                me.myData.list[i].MinPlaus = minPlaus;
                me.myData.list[i].MaxPlaus = maxPlaus;
                me.myData.list[i].Timer = timer;
            }

            me.updateData({ TypeId: typeid, EditIndex: me.myStatus.index, List: me.myData.list }, function (result) {
                me.myHtml.content.details.save.button("disable");
              
                //check response content
                if (result == false) { me.showMessage(me.resources.err_saveData, true); return true; }
                me._back();
                me._fetch();
            });


        } catch (e) { me._writeErr(e); return false; }
        return true;
    },

    /**
     * Deletes the currently selected entry from the list and persists the change to the server.
     *
     * This method:
     *   1. Shows a loading indicator in the list area.
     *   2. Removes the entry at myStatus.index from the local data array using splice().
     *   3. Sends the complete updated list (with the entry removed) to the server via updateData().
     *   4. On success: Navigates back to the list view and re-fetches the data.
     *      On failure: Shows an error message.
     *
     * Note: This method is called after the user confirms the deletion in the ask dialog.
     *
     * @param {number} index - The array index of the entry to delete (from the delete button's data attribute).
     * @returns {boolean} true if the delete was initiated, false on error.
     */
    _delete: function (index) {
        var me = this;
        try {
            me._setLoadingIcon(me.myHtml.content.list.entries);

            me.myData.list.splice(index, 1);

            me.updateData({ TypeId: 3, EditIndex: index, List: me.myData.list }, function (result) {
                me.myHtml.content.details.save.button("disable");

                //check response content
                if (result == false) { me.showMessage(me.resources.err_saveData, true); return true; }
                me._back();
                me._fetch();
            });


        } catch (e) { me._writeErr(e); return false; }
        return true;
    },
    /**
     * Calculates and applies responsive heights to all major layout containers.
     * The available content height is determined by subtracting the menu bar height
     * and padding from the total visual height provided by the framework.
     *
     * Applied to:
     *   - Content wrapper: Overall content area below the menu.
     *   - List wrapper: The list panel container.
     *   - List entries: The scrollable entry area (slightly reduced for the header).
     *   - Detail wrapper: The edit form panel (same height as list for consistent layout).
     *
     * @returns {boolean} true if heights were set, false if the widget is in an error state.
     */
    _setHeightsAndWidths: function () {
        var me = this;
        try {
            if (me.error) { return false; }
            let details = me.myHtml.content.details;
            // Calculate available content height: total visual height minus menu and padding.
            let hList = me._getVisualHeight() - me.myHtml.menu.wrapper.outerHeight() - 14;

            // Apply the calculated height to both content columns.
            me.myHtml.content.wrapper.css("height", hList);
            me.myHtml.content.list.wrapper.css("height", hList);
            me.myHtml.content.list.entries.css("height", hList - 45);
            details.wrapper.css("height", hList);

        } catch (e) { me._writeErr(e); return false; }
        return true;
    },
    /**
     * Handles error display states for the widget.
     * When an error occurs (bError=true), both the menu bar and content area are hidden,
     * leaving only the framework's error message visible to the user.
     * This ensures a clean UI when the widget cannot function due to missing data or server errors.
     *
     * Called by the framework's built-in error handling mechanism (inherited from vcBaseV4).
     *
     * @param {boolean} bError - true if an error state should be shown, false otherwise.
     * @returns {boolean} true on success, false on error.
     */
    specificInfoHandling: function (bError) {
        var me = this;
        try {
            if (bError) {
                // Hide both menu and content to show only the error message.
                me.myHtml.menu.wrapper.hide();
                me.myHtml.content.wrapper.hide();
            }

        } catch (e) { me._writeErr(e); return false; }
        return true;
    }
});